<script setup lang="ts">
import { ref } from 'vue'
import VideoModal from './VideoModal.vue'

const videos = [
  {
    id: 'pos-basics',
    icon: '🏪',
    title: 'POS Basics',
    description: 'Learn the fundamentals of our POS system',
    thumbnail: '/videos/thumbnails/pos-basics.jpg',
    videoUrl: '/videos/pos-basics.mp4'
  },
  {
    id: 'inventory-tracking',
    icon: '📦',
    title: 'Inventory Tracking',
    description: 'Real-time inventory management demo',
    thumbnail: '/videos/thumbnails/inventory-tracking.jpg',
    videoUrl: '/videos/inventory-tracking.mp4'
  },
  {
    id: 'financial-reports',
    icon: '📊',
    title: 'Financial Reports',
    description: 'Generate and analyze financial reports',
    thumbnail: '/videos/thumbnails/financial-reports.jpg',
    videoUrl: '/videos/financial-reports.mp4'
  },
  {
    id: 'customer-management',
    icon: '👥',
    title: 'Customer Management',
    description: 'Managing customer profiles and loyalty',
    thumbnail: '/videos/thumbnails/customer-management.jpg',
    videoUrl: '/videos/customer-management.mp4'
  },
  {
    id: 'sales-analytics',
    icon: '📈',
    title: 'Sales Analytics',
    description: 'Advanced sales reporting and insights',
    thumbnail: '/videos/thumbnails/sales-analytics.jpg',
    videoUrl: '/videos/sales-analytics.mp4'
  },
  {
    id: 'multi-location',
    icon: '🏢',
    title: 'Multi-location Management',
    description: 'Managing multiple store locations',
    thumbnail: '/videos/thumbnails/multi-location.jpg',
    videoUrl: '/videos/multi-location.mp4'
  },
  {
    id: 'employee-management',
    icon: '👤',
    title: 'Employee Management',
    description: 'Staff scheduling and performance tracking',
    thumbnail: '/videos/thumbnails/employee-management.jpg',
    videoUrl: '/videos/employee-management.mp4'
  },
  {
    id: 'promotions',
    icon: '🎯',
    title: 'Promotions & Discounts',
    description: 'Setting up promotional campaigns',
    thumbnail: '/videos/thumbnails/promotions.jpg',
    videoUrl: '/videos/promotions.mp4'
  },
  {
    id: 'integrations',
    icon: '🔌',
    title: 'Third-party Integrations',
    description: 'Connecting with other business tools',
    thumbnail: '/videos/thumbnails/integrations.jpg',
    videoUrl: '/videos/integrations.mp4'
  }
]

const selectedVideo = ref<typeof videos[0] | null>(null)

const playVideo = (video: typeof videos[0]) => {
  selectedVideo.value = video
}
</script>

<template>
  <section class="py-16 bg-gray-50 dark:bg-gray-800/50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-12">
        <h2 class="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          <span class="mr-2">🎥</span>
          Feature Videos
        </h2>
        <p class="text-lg text-gray-600 dark:text-gray-400">
          Watch our features in action
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div
          v-for="video in videos"
          :key="video.id"
          class="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg group cursor-pointer"
          @click="playVideo(video)"
        >
          <!-- Video Thumbnail -->
          <div class="relative aspect-video bg-gray-200 dark:bg-gray-700">
            <!-- Placeholder/Thumbnail -->
            <img
              :src="video.thumbnail"
              :alt="video.title"
              class="w-full h-full object-cover"
            />
            
            <!-- Play Button Overlay -->
            <div class="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
              <div
                class="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center text-blue-600 transform scale-90 group-hover:scale-100 transition-transform"
              >
                <span class="text-4xl">▶️</span>
              </div>
            </div>
          </div>

          <!-- Video Info -->
          <div class="p-6">
            <h3 class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-2">
              <span class="text-2xl">{{ video.icon }}</span>
              {{ video.title }}
            </h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ video.description }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Video Modal -->
    <VideoModal
      v-if="selectedVideo"
      :video-url="selectedVideo.videoUrl"
      :title="selectedVideo.title"
      @close="selectedVideo = null"
    />
  </section>
</template>